/**
 * useFlowState — URL-driven state for flow builders.
 *
 * Parameterized by FlowSource so the same hook works for
 * Event Flow, Screen Flow, and any future flow section.
 *
 * Example URL:
 *   /event-flow/new?preset=last_7_days&collapse=true&filterMode=exclude&events=ad_impression
 */
import { useCallback, useMemo } from 'react';
import { resolveDateRange } from '@/lib/dates';
import { useBulkUrlState } from '@/hooks/useUrlState';
import type { FlowState, FlowSource, FilterMode } from './flow.types';

const DEFAULTS: FlowState = {
  start: '',
  end: '',
  preset: 'last_7_days',
  filterMode: 'exclude',
  events: [],
  startFrom: '',
  collapse: false,
  kpi: '',
  filters: '',
};

/**
 * Hook for flow builder URL state.
 *
 * @param source - Flow source type (determines API `source` param)
 */
export function useFlowState(source: FlowSource) {
  const { state: rawState, update, reset, searchParams } = useBulkUrlState(DEFAULTS, {
    arrayKeys: ['events'],
    booleanKeys: ['collapse'],
    replace: true,
  });

  // Resolve preset vs explicit dates
  const state = useMemo<FlowState>(() => {
    const preset =
      searchParams.has('start') && !searchParams.has('preset')
        ? ''
        : (rawState.preset as string);
    return {
      ...rawState,
      preset,
      filterMode: (rawState.filterMode || 'exclude') as FilterMode,
    } as FlowState;
  }, [rawState, searchParams]);

  /** Resolved start/end dates */
  const dates = useMemo(
    () => resolveDateRange(state.preset, state.start, state.end),
    [state.preset, state.start, state.end],
  );

  /** Can run: always true for flow — no strict prerequisite like table+events */
  const canRun = useMemo(() => {
    return Boolean(dates.startDate && dates.endDate);
  }, [dates]);

  /** Build the API request body for /flow/data (supports depth pagination) */
  const toFlowPayload = useCallback((depthFrom?: number, depthTo?: number) => {
    return {
      source,
      startDate: dates.startDate,
      endDate: dates.endDate,
      collapseRepeats: state.collapse,
      excludedEvents: state.events,
      filterMode: state.filterMode,
      filters: state.filters ? decodeFiltersFromUrl(state.filters) : [],
      startFromEvent: state.startFrom || undefined,
      kpiEvent: state.kpi || undefined,
      ...(depthFrom != null ? { depthFrom } : {}),
      ...(depthTo != null ? { depthTo } : {}),
    };
  }, [source, state, dates]);

  /** Build the API request body for /flow/continuation */
  const toContinuationPayload = useCallback((sessionPaths: string[][], depthTo?: number) => {
    // Continuation sessions should clip from the standard session-start event,
    // not the original startFromEvent (which may be a one-time event like app_first_opened).
    const CONTINUATION_START: Record<FlowSource, string> = {
      event_flow: 'app_launch',
      screen_flow: '', // will be set when screen flow is implemented
    };
    return {
      source,
      startDate: dates.startDate,
      endDate: dates.endDate,
      collapseRepeats: state.collapse,
      excludedEvents: state.events,
      filterMode: state.filterMode,
      filters: state.filters ? decodeFiltersFromUrl(state.filters) : [],
      startFromEvent: state.startFrom || undefined,
      continuationStartFrom: CONTINUATION_START[source] || undefined,
      kpiEvent: state.kpi || undefined,
      sessionPaths,
      ...(depthTo != null ? { depthTo } : {}),
    };
  }, [source, state, dates]);

  /** Build the API request body for /flow/time-series */
  const toTimeSeriesPayload = useCallback(
    (path: string[], isTerminal: boolean) => {
      return {
        source,
        startDate: dates.startDate,
        endDate: dates.endDate,
        collapseRepeats: state.collapse,
        path,
        isTerminal,
        excludedEvents: state.events,
        filterMode: state.filterMode,
        filters: state.filters ? decodeFiltersFromUrl(state.filters) : [],
        startFromEvent: state.startFrom || undefined,
      };
    },
    [source, state, dates],
  );

  return { state, update, reset, canRun, toFlowPayload, toContinuationPayload, toTimeSeriesPayload, dates, searchParams };
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

/**
 * Decode base64-encoded filters from the URL into backend FilterItem[] format.
 * Supports grouped and legacy flat formats (same as useProductState).
 */
function decodeFiltersFromUrl(encoded: string): unknown[] {
  try {
    const parsed = JSON.parse(atob(encoded));

    if (parsed && Array.isArray(parsed.groups)) {
      const groups = parsed.groups as Array<{
        conditions: Array<{ field: string; operator: string; value: string }>;
        conditionOperator?: 'AND' | 'OR';
      }>;
      const validGroups = groups.filter((g) =>
        g.conditions.some((c) => c.field && c.value),
      );
      if (validGroups.length === 0) return [];

      if (validGroups.length === 1) {
        const condOp = validGroups[0].conditionOperator ?? 'AND';
        return validGroups[0].conditions
          .filter((c) => c.field && c.value)
          .map((c, i) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: i === 0 ? null : condOp,
          }));
      }

      const topOp = parsed.groupOperator ?? 'OR';
      return validGroups.map((group, gIdx) => ({
        type: 'group' as const,
        groupOperator: group.conditionOperator ?? 'AND',
        operator: gIdx === 0 ? null : topOp,
        children: group.conditions
          .filter((c) => c.field && c.value)
          .map((c, cIdx) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: cIdx === 0 ? null : (group.conditionOperator ?? 'AND'),
          })),
      }));
    }

    if (Array.isArray(parsed)) {
      return parsed
        .filter((f: { field: string; value: string }) => f.field && f.value)
        .map((f: { field: string; operator: string; value: string }) => ({
          type: 'filter' as const,
          field: f.field,
          value: f.value,
          comparison: f.operator || '=',
          operator: null,
        }));
    }

    return [];
  } catch {
    return [];
  }
}
